import axios from 'axios';

// ALWAYS use current origin for API calls - this ensures the app works on ANY domain
export const getApiUrl = () => {
  // Always use the current domain's origin for API calls
  // This makes the app work on preview, production, or any other domain
  return window.location.origin + '/api';
};

export const API_URL = getApiUrl();

// Create axios instance with retry logic
const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 30000, // Increased timeout
});

// Track recent errors to prevent spam
let recentErrors = {};
const ERROR_THROTTLE_MS = 5000;

const shouldShowError = (errorKey) => {
  const now = Date.now();
  if (recentErrors[errorKey] && now - recentErrors[errorKey] < ERROR_THROTTLE_MS) {
    return false;
  }
  recentErrors[errorKey] = now;
  return true;
};

// Retry logic for failed requests
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    
    // Don't retry if it's a 4xx error (client error) or already retried twice
    if (!config || config._retryCount >= 2 || (error.response && error.response.status >= 400 && error.response.status < 500)) {
      return Promise.reject(error);
    }
    
    // Track retry count
    config._retryCount = (config._retryCount || 0) + 1;
    
    // Exponential backoff: 1s, then 2s
    const delay = config._retryCount * 1000;
    await new Promise(resolve => setTimeout(resolve, delay));
    
    return apiClient(config);
  }
);

// Clean up old error entries periodically
setInterval(() => {
  const now = Date.now();
  Object.keys(recentErrors).forEach(key => {
    if (now - recentErrors[key] > ERROR_THROTTLE_MS * 2) {
      delete recentErrors[key];
    }
  });
}, 10000);

// Export for use in components
export { shouldShowError };

// Onboarding
export const getOnboardingStatus = () => axios.get(`${API_URL}/onboarding/status`);
export const updateOnboardingStep = (step, data = {}) => axios.post(`${API_URL}/onboarding/step`, { step, data });
export const completeOnboarding = () => axios.post(`${API_URL}/onboarding/complete`);
export const skipOnboarding = () => axios.post(`${API_URL}/onboarding/skip`);
export const resetOnboarding = () => axios.post(`${API_URL}/onboarding/reset`);

// Account
export const changeEmail = (newEmail) => axios.post(`${API_URL}/auth/change-email`, { new_email: newEmail });
export const adminUpdateUser = (userId, data) => axios.put(`${API_URL}/admin/users/${userId}`, data);
export const adminSetMembership = (userId, data) => axios.put(`${API_URL}/admin/users/${userId}/membership`, data);
export const adminClearMembership = (userId) => axios.delete(`${API_URL}/admin/users/${userId}/membership`);

// Categories
export const getCategories = () => axios.get(`${API_URL}/categories`);
export const createCategory = (data) => axios.post(`${API_URL}/categories`, data);
export const updateCategory = (id, data) => axios.put(`${API_URL}/categories/${id}`, data);
export const deleteCategory = (id) => axios.delete(`${API_URL}/categories/${id}`);

// Subcategories
export const getSubcategories = (categoryId) => 
  axios.get(`${API_URL}/subcategories`, { params: { category_id: categoryId } });
export const createSubcategory = (data) => axios.post(`${API_URL}/subcategories`, data);
export const updateSubcategory = (id, data) => axios.put(`${API_URL}/subcategories/${id}`, data);
export const deleteSubcategory = (id) => axios.delete(`${API_URL}/subcategories/${id}`);

// Skills
export const getSkills = (subcategoryId) => 
  axios.get(`${API_URL}/skills`, { params: { subcategory_id: subcategoryId } });
export const getSkill = (id) => axios.get(`${API_URL}/skills/${id}`);
export const createSkill = (data) => axios.post(`${API_URL}/skills`, data);
export const updateSkill = (id, data) => axios.put(`${API_URL}/skills/${id}`, data);
export const deleteSkill = (id) => axios.delete(`${API_URL}/skills/${id}`);

// Progress
export const getProgress = () => axios.get(`${API_URL}/progress`);
export const updateProgress = (skillId, checklistItemIndex, completed) => 
  axios.post(`${API_URL}/progress/update`, { skill_id: skillId, checklist_item_index: checklistItemIndex, completed });
export const getProgressStats = () => axios.get(`${API_URL}/progress/stats`);

// Skill Completion & Notes
export const markSkillComplete = (skillId, completed) => 
  axios.post(`${API_URL}/skills/${skillId}/complete`, { skill_id: skillId, completed });
export const getSkillCompletion = (skillId) => axios.get(`${API_URL}/skills/${skillId}/completion`);
export const updateSkillNotes = (skillId, notes, willAddToGame) => 
  axios.post(`${API_URL}/skills/${skillId}/notes`, { skill_id: skillId, notes, will_add_to_game: willAddToGame });
export const getSkillNotes = (skillId) => axios.get(`${API_URL}/skills/${skillId}/notes`);

// Needs Work
export const toggleNeedsWork = (skillId, needsWork) => 
  axios.post(`${API_URL}/skills/${skillId}/needs-work`, { skill_id: skillId, needs_work: needsWork });
export const getNeedsWork = (skillId) => axios.get(`${API_URL}/skills/${skillId}/needs-work`);

// Player History
export const getPlayerHistory = () => axios.get(`${API_URL}/player/history`);
export const getAllSkillStatus = () => axios.get(`${API_URL}/player/skill-status`);

// Coach Suggestions
export const suggestSkillToPlayer = (playerId, skillId, note = '') => 
  axios.post(`${API_URL}/coach/suggest-skill`, { player_id: playerId, skill_id: skillId, note });
export const removeSuggestion = (suggestionId) => axios.delete(`${API_URL}/coach/suggestion/${suggestionId}`);
export const getPlayerHistoryForCoach = (playerId) => axios.get(`${API_URL}/coach/player/${playerId}/history`);

// File Upload
export const uploadVideo = async (file, onProgress = null) => {
  const CHUNK_SIZE = 2 * 1024 * 1024; // 2MB chunks - small enough for proxy limits
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
  
  console.log(`[Upload] Starting upload: ${file.name}, size: ${file.size}, type: ${file.type}`);
  
  // For small files (< 5MB), use direct upload
  if (file.size < 5 * 1024 * 1024) {
    console.log('[Upload] Using direct upload for small file');
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_URL}/upload/video`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
      onUploadProgress: onProgress ? (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(percentCompleted);
      } : undefined
    });
  }
  
  // For larger files, use chunked upload
  console.log(`[Upload] Using chunked upload: ${totalChunks} chunks`);
  try {
    // Step 1: Initialize upload
    console.log('[Upload] Initializing chunked upload...');
    const initRes = await axios.post(`${API_URL}/upload/chunked/init`, {
      filename: file.name,
      total_size: file.size,
      total_chunks: totalChunks
    });
    const uploadId = initRes.data.upload_id;
    console.log(`[Upload] Got upload ID: ${uploadId}`);
    
    // Step 2: Upload chunks
    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);
      
      console.log(`[Upload] Uploading chunk ${i + 1}/${totalChunks}`);
      const formData = new FormData();
      formData.append('file', chunk, `chunk_${i}`);
      
      await axios.post(`${API_URL}/upload/chunked/${uploadId}/${i}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000
      });
      
      if (onProgress) {
        const progress = Math.round(((i + 1) / totalChunks) * 100);
        onProgress(progress);
      }
    }
    
    // Step 3: Complete upload
    console.log('[Upload] Completing chunked upload...');
    const completeRes = await axios.post(`${API_URL}/upload/chunked/complete`, {
      upload_id: uploadId,
      filename: file.name
    });
    
    console.log('[Upload] Upload complete!', completeRes.data);
    return { data: completeRes.data };
  } catch (error) {
    console.error('[Upload] Chunked upload error:', error.response?.data || error.message);
    throw error;
  }
};
export const getUploads = () => axios.get(`${API_URL}/uploads`);

// Favorites
export const getFavorites = () => axios.get(`${API_URL}/favorites`);
export const toggleFavorite = (skillId) => axios.post(`${API_URL}/favorites/toggle`, { skill_id: skillId });
export const checkFavorite = (skillId) => axios.get(`${API_URL}/favorites/check/${skillId}`);

// Messages
export const getConversations = () => axios.get(`${API_URL}/messages/conversations`);
export const getMessages = (partnerId) => axios.get(`${API_URL}/messages/${partnerId}`);
export const sendMessage = (receiverId, content) => axios.post(`${API_URL}/messages`, { receiver_id: receiverId, content });

// Coach
export const getPlayers = () => axios.get(`${API_URL}/coach/players`);
export const getPlayerProgress = (playerId) => axios.get(`${API_URL}/coach/player/${playerId}/progress`);
export const getCoaches = () => axios.get(`${API_URL}/users/coaches`);

// Profile
export const updateProfile = (data) => axios.put(`${API_URL}/profile`, data);
export const uploadProfilePicture = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return axios.post(`${API_URL}/profile/picture`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const getUserProfile = (userId) => axios.get(`${API_URL}/profile/${userId}`);

// Notifications
export const getNotifications = () => axios.get(`${API_URL}/notifications`);
export const markNotificationRead = (notificationId) => axios.post(`${API_URL}/notifications/${notificationId}/read`);
export const markAllNotificationsRead = () => axios.post(`${API_URL}/notifications/read-all`);
export const getUnreadNotificationCount = () => axios.get(`${API_URL}/notifications/unread-count`);

// Skill Reordering
export const reorderSkills = (subcategoryId, skillIds) => axios.put(`${API_URL}/skills/reorder`, { skill_ids: skillIds });
export const reorderSubcategories = (subcategoryIds) => axios.put(`${API_URL}/subcategories/reorder`, { skill_ids: subcategoryIds });
export const reorderCategories = (categoryIds) => axios.put(`${API_URL}/categories/reorder`, { skill_ids: categoryIds });

// Bulk Suggestions
export const suggestSkillsBulk = (playerId, skillIds, note = '') => 
  axios.post(`${API_URL}/coach/suggest-skills-bulk`, { player_id: playerId, skill_ids: skillIds, note });
export const suggestSkillWithNotification = (playerId, skillId, note = '') => 
  axios.post(`${API_URL}/coach/suggest-skill-v2`, { player_id: playerId, skill_id: skillId, note });

// Suggestion Tracking
export const markSuggestionViewed = (suggestionId) => axios.post(`${API_URL}/suggestions/${suggestionId}/viewed`);
export const markSuggestionCompleted = (suggestionId) => axios.post(`${API_URL}/suggestions/${suggestionId}/completed`);
export const getSuggestionStats = () => axios.get(`${API_URL}/coach/suggestions/stats`);

// Rankings
export const getRankings = () => axios.get(`${API_URL}/rankings`);
export const updateRankings = (playerIds) => axios.put(`${API_URL}/rankings`, { player_ids: playerIds });
export const autoGenerateRankings = () => axios.get(`${API_URL}/rankings/auto-generate`);

// Welcome Video
export const getWelcomeVideo = () => axios.get(`${API_URL}/welcome-video`);
export const updateWelcomeVideo = (videoUrl, videoType) => 
  axios.put(`${API_URL}/welcome-video`, { video_url: videoUrl, video_type: videoType });

// Shot Tracker
export const logShots = (shotType, made, attempted, notes = '') => 
  axios.post(`${API_URL}/shots/log`, { shot_type: shotType, made, attempted, notes });
export const getShotHistory = (limit = 50) => axios.get(`${API_URL}/shots/history`, { params: { limit } });
export const getShotStats = () => axios.get(`${API_URL}/shots/stats`);
export const getShotMonthlyTrend = () => axios.get(`${API_URL}/shots/monthly-trend`);
export const getPersonalBests = () => axios.get(`${API_URL}/player/personal-bests`);
export const getPlayerShotStats = (playerId) => axios.get(`${API_URL}/coach/player/${playerId}/shots`);
export const deleteShot = (shotId) => axios.delete(`${API_URL}/shots/${shotId}`);

// Court Shot Chart
export const logCourtShot = (x, y, made) => 
  axios.post(`${API_URL}/shots/court`, { x, y, made });
export const getCourtShots = (days = 30) => 
  axios.get(`${API_URL}/shots/court`, { params: { days } });
export const getCourtShotStats = () => axios.get(`${API_URL}/shots/court/stats`);
export const deleteCourtShot = (shotId) => axios.delete(`${API_URL}/shots/court/${shotId}`);
export const clearCourtShots = () => axios.delete(`${API_URL}/shots/court/clear`);

// Player Invitations
export const generateInvitation = () => axios.post(`${API_URL}/coach/invitations/generate`);
export const getInvitations = () => axios.get(`${API_URL}/coach/invitations`);
export const deleteInvitation = (invitationId) => axios.delete(`${API_URL}/coach/invitations/${invitationId}`);
export const sendInvitationEmail = (email, playerName = '', message = '') => 
  axios.post(`${API_URL}/coach/invitations/send-email`, { email, player_name: playerName, message });
export const validateInvitationCode = (code) => axios.post(`${API_URL}/auth/validate-code?code=${code}`);
export const registerWithCode = (userData, code) => 
  axios.post(`${API_URL}/auth/register-with-code?code=${code}`, userData);

// Q&A System
export const createQuestion = (title, content, skillId = null) => 
  axios.post(`${API_URL}/questions`, { title, content, skill_id: skillId });
export const getQuestions = (status = null, skillId = null) => 
  axios.get(`${API_URL}/questions`, { params: { status, skill_id: skillId } });
export const getQuestion = (questionId) => axios.get(`${API_URL}/questions/${questionId}`);
export const answerQuestion = (questionId, content) => 
  axios.post(`${API_URL}/questions/${questionId}/answer`, { content });
export const closeQuestion = (questionId) => axios.put(`${API_URL}/questions/${questionId}/close`);
export const deleteQuestion = (questionId) => axios.delete(`${API_URL}/questions/${questionId}`);
export const getQuestionStats = () => axios.get(`${API_URL}/coach/questions/stats`);

// Community Feed
export const createPost = (title, content, videoUrl = '', videoType = '') => 
  axios.post(`${API_URL}/posts`, { title, content, video_url: videoUrl, video_type: videoType });
export const getPosts = (limit = 50, skip = 0) => 
  axios.get(`${API_URL}/posts`, { params: { limit, skip } });
export const getPost = (postId) => axios.get(`${API_URL}/posts/${postId}`);
export const updatePost = (postId, data) => axios.put(`${API_URL}/posts/${postId}`, data);
export const deletePost = (postId) => axios.delete(`${API_URL}/posts/${postId}`);
export const togglePostLike = (postId) => axios.post(`${API_URL}/posts/${postId}/like`);
export const uploadLargeVideo = (file, onProgress = null) => {
  const formData = new FormData();
  formData.append('file', file);
  return axios.post(`${API_URL}/upload/video-large`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: onProgress ? (e) => onProgress(Math.round((e.loaded * 100) / e.total)) : undefined
  });
};

// Post Comments
export const getPostComments = (postId) => axios.get(`${API_URL}/posts/${postId}/comments`);
export const addPostComment = (postId, content) => axios.post(`${API_URL}/posts/${postId}/comments`, { content });
export const deletePostComment = (postId, commentId) => axios.delete(`${API_URL}/posts/${postId}/comments/${commentId}`);

// Workout Planner
export const createWorkout = (title, description = '', scheduledDate = '', skillIds = [], notes = '') => 
  axios.post(`${API_URL}/workouts`, { title, description, scheduled_date: scheduledDate, skill_ids: skillIds, notes });
export const getWorkouts = () => axios.get(`${API_URL}/workouts`);
export const getWorkout = (workoutId) => axios.get(`${API_URL}/workouts/${workoutId}`);
export const updateWorkout = (workoutId, data) => axios.put(`${API_URL}/workouts/${workoutId}`, data);
export const deleteWorkout = (workoutId) => axios.delete(`${API_URL}/workouts/${workoutId}`);
export const toggleWorkoutComplete = (workoutId) => axios.post(`${API_URL}/workouts/${workoutId}/toggle-complete`);

// Game Tracker
export const logGame = (data) => axios.post(`${API_URL}/games/log`, data);
export const getGameHistory = (limit = 50) => axios.get(`${API_URL}/games/history`, { params: { limit } });
export const getGameStats = () => axios.get(`${API_URL}/games/stats`);
export const getGame = (gameId) => axios.get(`${API_URL}/games/${gameId}`);
export const updateGame = (gameId, data) => axios.put(`${API_URL}/games/${gameId}`, data);
export const deleteGame = (gameId) => axios.delete(`${API_URL}/games/${gameId}`);
export const getPlayerGameStats = (playerId) => axios.get(`${API_URL}/coach/player/${playerId}/games`);

// Coach Player Notes
export const getCoachPlayerNotes = (playerId) => axios.get(`${API_URL}/coach/player/${playerId}/notes`);
export const addCoachPlayerNote = (playerId, content) => axios.post(`${API_URL}/coach/player/${playerId}/notes`, { content });
export const updateCoachPlayerNote = (playerId, noteId, content) => axios.put(`${API_URL}/coach/player/${playerId}/notes/${noteId}`, { content });
export const deleteCoachPlayerNote = (playerId, noteId) => axios.delete(`${API_URL}/coach/player/${playerId}/notes/${noteId}`);

// Access & Subscription
export const getAccessStatus = () => axios.get(`${API_URL}/auth/access-status`);
export const getPricing = () => axios.get(`${API_URL}/pricing`);
export const createCheckoutSession = (planType, originUrl, withTrial = true) => axios.post(`${API_URL}/payments/create-checkout`, { plan_type: planType, origin_url: originUrl, with_trial: withTrial });
export const getPaymentStatus = (sessionId) => axios.get(`${API_URL}/payments/status/${sessionId}`);

// Subscription Management
export const getSubscriptionDetails = () => axios.get(`${API_URL}/subscription/details`);
export const cancelSubscription = (reason = '') => axios.post(`${API_URL}/subscription/cancel`, { reason });
export const reactivateSubscription = () => axios.post(`${API_URL}/subscription/reactivate`);
export const upgradeSubscription = (newPlan, originUrl) => axios.post(`${API_URL}/subscription/upgrade`, { new_plan: newPlan, origin_url: originUrl });

// Group Rankings
export const getGroupRankings = () => axios.get(`${API_URL}/rankings/groups`);
export const getSingleGroupRankings = (group) => axios.get(`${API_URL}/rankings/group/${group}`);
export const updateGroupRankings = (group, playerIds) => axios.put(`${API_URL}/rankings/group/${group}`, { group, player_ids: playerIds });
export const getPlayersByGroup = () => axios.get(`${API_URL}/players/by-group`);
export const updatePlayerGroup = (playerId, group) => axios.put(`${API_URL}/admin/player/${playerId}/group`, { player_id: playerId, group });

// 1v1 Head-to-Head Competition
export const logH2HMatch = (winnerId, loserId, group, score = null, notes = null) => 
  axios.post(`${API_URL}/h2h/log`, { winner_id: winnerId, loser_id: loserId, group, score, notes });
export const getH2HRecords = (group) => axios.get(`${API_URL}/h2h/records/${group}`);
export const getPlayerH2HRecord = (playerId) => axios.get(`${API_URL}/h2h/player/${playerId}`);
export const deleteH2HMatch = (matchId) => axios.delete(`${API_URL}/h2h/match/${matchId}`);

// Coach Analytics
export const getCoachAnalytics = () => axios.get(`${API_URL}/coach/analytics`);

// Admin Player/Coach Management
export const adminTagPlayer = (email, name) => axios.post(`${API_URL}/admin/tag-player`, { player_email: email, player_name: name });
export const adminUntagPlayer = (playerId) => axios.delete(`${API_URL}/admin/tag-player/${playerId}`);
export const adminAddCoach = (email, name) => axios.post(`${API_URL}/admin/add-coach`, { player_email: email, player_name: name });
export const adminCreateUser = (userData) => axios.post(`${API_URL}/admin/create-user`, userData);
export const getTaggedPlayers = () => axios.get(`${API_URL}/admin/tagged-players`);
export const getAdminCoachesList = () => axios.get(`${API_URL}/admin/coaches`);

// New admin player management
export const getAllPlayersForAdmin = () => axios.get(`${API_URL}/admin/all-players`);
export const adminTagPlayerById = (playerId) => axios.post(`${API_URL}/admin/tag-player/${playerId}`);
export const adminUntagPlayerById = (playerId) => axios.delete(`${API_URL}/admin/untag-player/${playerId}`);
export const adminLogShotsForPlayer = (playerId, data) => axios.post(`${API_URL}/admin/player/${playerId}/log-shots`, data);
export const getAdminPlayerStats = (playerId) => axios.get(`${API_URL}/admin/player/${playerId}/stats`);

// Coach Player Management
export const coachAddPlayer = (data) => axios.post(`${API_URL}/coach/add-player`, data);
export const coachRemovePlayer = (playerId) => axios.delete(`${API_URL}/coach/remove-player/${playerId}`);
export const getCoachMyPlayers = () => axios.get(`${API_URL}/coach/my-players`);
export const getCoachTraineeCode = () => axios.get(`${API_URL}/coach/trainee-code`);
export const getAllPlayersForCoach = () => axios.get(`${API_URL}/coach/players/list`);
export const coachLogShotsForPlayer = (playerId, shotData) => axios.post(`${API_URL}/coach/player/${playerId}/shots`, shotData);
export const coachLogShots = (data) => axios.post(`${API_URL}/coach/shots/log`, data);
export const coachLogGameForPlayer = (playerId, gameData) => axios.post(`${API_URL}/coach/player/${playerId}/games`, gameData);
export const coachLogGame = (data) => axios.post(`${API_URL}/coach/games/log`, data);
export const coachLogTeamGame = (data) => axios.post(`${API_URL}/coach/team/games`, data);
export const getCoachTeamGames = () => axios.get(`${API_URL}/coach/team/games`);
export const coachLogTeamTotalStats = (data) => axios.post(`${API_URL}/coach/team/total-stats`, data);
export const getCoachTeamTotalStats = () => axios.get(`${API_URL}/coach/team/total-stats`);
export const chargeExtraPlayer = () => axios.post(`${API_URL}/coach/charge-extra-player`);

// Coach Team Management
export const getCoachTeams = () => axios.get(`${API_URL}/coach/teams`);
export const createCoachTeam = (data) => axios.post(`${API_URL}/coach/teams`, data);
export const updateCoachTeam = (teamId, data) => axios.put(`${API_URL}/coach/teams/${teamId}`, data);
export const deleteCoachTeam = (teamId) => axios.delete(`${API_URL}/coach/teams/${teamId}`);
export const assignPlayerToTeam = (playerId, teamId) => axios.put(`${API_URL}/coach/players/${playerId}/team`, { team_id: teamId });
export const getCoachTeamRankings = (teamId) => axios.get(`${API_URL}/coach/teams/${teamId}/rankings`);
export const updateCoachTeamRankings = (teamId, playerIds) => axios.put(`${API_URL}/coach/teams/${teamId}/rankings`, { player_ids: playerIds });

// Team Seasons
export const getTeamSeasons = (teamId) => axios.get(`${API_URL}/coach/teams/${teamId}/seasons`);
export const createTeamSeason = (teamId, data) => axios.post(`${API_URL}/coach/teams/${teamId}/seasons`, data);
export const updateTeamSeason = (seasonId, data) => axios.put(`${API_URL}/coach/seasons/${seasonId}`, data);
export const deleteTeamSeason = (seasonId) => axios.delete(`${API_URL}/coach/seasons/${seasonId}`);

// Multi-Team Player Management
export const getTeamPlayers = (teamId) => axios.get(`${API_URL}/coach/teams/${teamId}/players`);
export const addPlayerToTeam = (teamId, data) => axios.post(`${API_URL}/coach/teams/${teamId}/players`, data);
export const removePlayerFromTeam = (teamId, playerId) => axios.delete(`${API_URL}/coach/teams/${teamId}/players/${playerId}`);

// Team Stats & Games
export const getTeamStats = (teamId, seasonId = null) => {
  const url = seasonId 
    ? `${API_URL}/coach/teams/${teamId}/stats?season_id=${seasonId}`
    : `${API_URL}/coach/teams/${teamId}/stats`;
  return axios.get(url);
};
export const logTeamGameWithPlayers = (teamId, data) => axios.post(`${API_URL}/coach/teams/${teamId}/games`, data);
export const getTeamGameHistory = (teamId) => axios.get(`${API_URL}/coach/teams/${teamId}/games`);
export const deleteTeamGame = (teamId, gameId) => axios.delete(`${API_URL}/coach/teams/${teamId}/games/${gameId}`);

// Coach Billing & Stripe
export const getCoachBillingInfo = () => axios.get(`${API_URL}/coach/billing/info`);
export const setupMeteredBilling = () => axios.post(`${API_URL}/coach/billing/setup-metered`);
export const updateExtraPlayerUsage = () => axios.post(`${API_URL}/coach/billing/update-usage`);
export const createSetupIntent = () => axios.post(`${API_URL}/coach/billing/create-setup-intent`);
export const confirmPaymentSetup = (setupIntentId) => axios.post(`${API_URL}/coach/billing/confirm-setup`, { setup_intent_id: setupIntentId });
export const createBillingPortalSession = (returnUrl) => axios.post(`${API_URL}/coach/billing/create-portal-session`, { return_url: returnUrl });

// Admin Coach Management
export const adminAssignPlayerToCoach = (data) => axios.post(`${API_URL}/admin/assign-player-to-coach`, data);
export const getCoachesList = () => axios.get(`${API_URL}/admin/coaches-list`);

// Password Management
export const changePassword = (currentPassword, newPassword) => 
  axios.post(`${API_URL}/auth/change-password`, { current_password: currentPassword, new_password: newPassword });

// Parent Dashboard
export const generateParentLink = (expiresInDays = 30) => {
  const token = localStorage.getItem('token');
  return axios.post(`${API_URL}/parents/generate-link?token=${token}`, { expires_in_days: expiresInDays });
};
export const getMyParentLinks = () => {
  const token = localStorage.getItem('token');
  return axios.get(`${API_URL}/parents/my-links?token=${token}`);
};
export const revokeParentLink = (linkCode) => {
  const token = localStorage.getItem('token');
  return axios.delete(`${API_URL}/parents/revoke-link/${linkCode}?token=${token}`);
};
export const getParentViewData = (linkCode) => axios.get(`${API_URL}/parents/view/${linkCode}`);


// Analytics
export const trackAnalyticsEvent = (eventType, page = null, element = null, metadata = null) => 
  axios.post(`${API_URL}/analytics/track`, { event_type: eventType, page, element, metadata }).catch(() => {});
export const getAnalyticsSummary = (days = 30) => axios.get(`${API_URL}/admin/analytics/summary`, { params: { days } });
export const getAnalyticsEvents = (limit = 100, eventType = null) => 
  axios.get(`${API_URL}/admin/analytics/events`, { params: { limit, event_type: eventType } });
export const getAnalyticsFunnel = (days = 30) => axios.get(`${API_URL}/admin/analytics/funnel`, { params: { days } });

// Abandoned Checkout Recovery
export const getAbandonedCheckouts = (hours = 24) => axios.get(`${API_URL}/admin/abandoned-checkouts`, { params: { hours } });
export const sendRecoveryEmail = (transactionId) => axios.post(`${API_URL}/admin/send-recovery-email/${transactionId}`);
export const sendBulkRecoveryEmails = (hours = 24) => axios.post(`${API_URL}/admin/send-bulk-recovery-emails`, null, { params: { hours } });

// AI Shot Analysis
export const analyzeShot = (imageFile) => {
  const formData = new FormData();
  formData.append('image', imageFile);
  return axios.post(`${API_URL}/ai/analyze-shot`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const getAnalysisHistory = (limit = 20) => axios.get(`${API_URL}/ai/analysis-history`, { params: { limit } });

// Referral Program
export const getMyReferralCode = () => axios.get(`${API_URL}/referral/my-code`);
export const getMyReferrals = () => axios.get(`${API_URL}/referral/my-referrals`);
export const validateReferralCode = (code) => axios.get(`${API_URL}/referral/validate/${code}`);

// Public Profiles
export const togglePublicProfile = () => axios.post(`${API_URL}/profile/toggle-public`);
export const getPublicProfileStatus = () => axios.get(`${API_URL}/profile/public-status`);
export const getPublicProfile = (slug) => axios.get(`${API_URL}/p/${slug}`);

// Training Content Management
export const getTrainingSections = () => axios.get(`${API_URL}/training/sections`);
export const createTrainingSection = (data) => axios.post(`${API_URL}/training/sections`, data);
export const updateTrainingSection = (sectionId, data) => axios.put(`${API_URL}/training/sections/${sectionId}`, data);
export const deleteTrainingSection = (sectionId) => axios.delete(`${API_URL}/training/sections/${sectionId}`);
export const reorderTrainingSections = (itemIds) => axios.put(`${API_URL}/training/sections/reorder`, { item_ids: itemIds });

export const getTrainingSubsections = (sectionId = null) => {
  const url = sectionId 
    ? `${API_URL}/training/subsections?section_id=${sectionId}`
    : `${API_URL}/training/subsections`;
  return axios.get(url);
};
export const createTrainingSubsection = (data) => axios.post(`${API_URL}/training/subsections`, data);
export const updateTrainingSubsection = (subsectionId, data) => axios.put(`${API_URL}/training/subsections/${subsectionId}`, data);
export const deleteTrainingSubsection = (subsectionId) => axios.delete(`${API_URL}/training/subsections/${subsectionId}`);
export const reorderTrainingSubsections = (itemIds) => axios.put(`${API_URL}/training/subsections/reorder`, { item_ids: itemIds });

export const getTrainingContent = (subsectionId = null) => {
  const url = subsectionId 
    ? `${API_URL}/training/content?subsection_id=${subsectionId}`
    : `${API_URL}/training/content`;
  return axios.get(url);
};
export const getTrainingContentItem = (contentId) => axios.get(`${API_URL}/training/content/${contentId}`);
export const createTrainingContent = (data) => axios.post(`${API_URL}/training/content`, data);
export const updateTrainingContent = (contentId, data) => axios.put(`${API_URL}/training/content/${contentId}`, data);
export const deleteTrainingContent = (contentId) => axios.delete(`${API_URL}/training/content/${contentId}`);
export const reorderTrainingContent = (itemIds) => axios.put(`${API_URL}/training/content/reorder`, { item_ids: itemIds });

export const getTrainingTree = () => axios.get(`${API_URL}/training/tree`);

// Training Progress (favorites, completed, watch later)
export const getUserTrainingProgress = () => axios.get(`${API_URL}/training/user-progress`);
export const markTrainingContent = (contentId, markType, marked) => 
  axios.post(`${API_URL}/training/mark`, { content_id: contentId, mark_type: markType, marked });
export const getMarkedContent = (markType = null) => {
  const url = markType 
    ? `${API_URL}/training/marked-content?mark_type=${markType}`
    : `${API_URL}/training/marked-content`;
  return axios.get(url);
};
